import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  FilePen,
  FileSignature,
  FileText,
  History,
  MoreHorizontal,
  PanelRightClose,
  PanelRightOpen,
  RefreshCw,
  Send,
  ShieldCheck,
  Unlock,
  Upload,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { ApiError, api, background } from "../../api/client";
import type { Order } from "../../api/types";
import { attentionSelector, designate, designateSoon } from "../../components/attention";
import { Banner } from "../../components/Callout";
import { HigherOrderBanner } from "../declination/HigherOrderBanner";
import { ChiefOrderBanner } from "../received/ChiefOrderNotice";
import { PendingMissionDraftBanner } from "../PendingMissionDraftBanner";
import { Dialog, DialogBody } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { GenerationVeil, GenerationWait } from "../../components/GenerationVeil";
import { Menu, MenuItem } from "../../components/Menu";
import { Motion, Presence } from "../../components/presence";
import { SidePanel } from "../../components/SidePanel";
import { Badge, Button, IconButton, Segmented } from "../../components/ui";
import { useMediaQuery } from "../../components/useMediaQuery";
import { ViewHeader } from "../../components/ViewHeader";
import { TRANSMITTING } from "../../echelons";
import { k, locale, t } from "../../i18n/i18n";
import { useTrialExport } from "../../licence/trial";
import { plural } from "../../plural";
import { useUi, type ViewKey } from "../../state/ui";
import { gateNeeded, useCoherence } from "../coherence/coherenceState";

/** Validation suspendue : des points de cohérence tactique restent ouverts, le panneau s'ouvre d'abord. */
class CoherenceGateSignal extends Error {}
import { ORDER_REOPEN } from "./attentionTargets";
import { DossierEmptyState } from "../DossierEmptyState";
import { ExportMenu } from "./ExportMenu";
import { CompareDialog } from "./CompareDialog";
import { OrderEditor } from "./OrderEditor";
import { editedFields, outdatedFields, outdatedPair } from "./outdated";
import { term } from "../../i18n/vocabulary";
import { refreshOrders, rememberOrderSpecs, sectionList, subordinatesNote, takeChangesFocus } from "../../map/orderSync";
import { licenceStorage } from "../../state/licenceScope";
import {
  beforeAfter,
  changedFields,
  changedSections,
  changedZones,
  chiefOf,
  freshKeys,
  latestChange,
  orderChanges,
  readSeen,
  reviewBannerText,
  writeSeen,
} from "./changes";
import type { ZoneChange } from "./OrderPage";
import { OrderPage } from "./OrderPage";
import { UnitPeek, type UnitMention, unitMentions } from "./UnitPeek";
import { useDeployment } from "../../map/units/useDeployment";
import { orderSaveQueue, type SaveSnapshot } from "./orderSaveQueue";
import { FragoView } from "./FragoView";
import { ResultsView } from "./ResultsView";
import { currentStep, type Opened, rotationSteps, stepAction, wingoNumber } from "./rotationSteps";
import { RotationTimeline } from "./RotationTimeline";
import { WingoView } from "./WingoView";
import { SignaturePad } from "./SignaturePad";
import { TransmitDialog } from "./TransmitDialog";
import { designateZoneWait, handleZoneRefusal } from "../../map/zones/zoneAttention";
import { usePendingZone, ZoneWaitBanner, ZoneWaitCallout } from "../../map/zones/ZoneWaitCallout";
import { designateReadOnly } from "../../licence/ReadOnlyLicence";
import { designateDossierReadOnly, useDossierAccess } from "../../sharing/access";
import { DossierReadOnlyNote } from "../../sharing/ReadOnlyBanner";
import { Waiting } from "../../components/Waiting";
import {
  announceApproved,
  announceOplanApproved,
  announceReopened,
  ApproveButton,
  approvalExpected,
  blockingIssue,
  designateAfterWrite,
  designateApproval,
  designateIssue,
  ORDER_CHANGES_NEXT,
  saveSettled,
} from "./approval";
import { briefingKey } from "../briefing/briefingJob";
import { countrySheetKey } from "../country/countryJob";
import { sheetWritten } from "../country/countrySheet";
import { orderImageUrl } from "./mapImages";
import { rotationCode } from "../../centacRotation";
import { CENTAC_ROTATION_FIELD } from "../missionDataTarget";
import { showMissionField } from "../openMissionData";
import { TranslationBanner, translationInterval } from "../translation/TranslationBanner";

const REASONS: Record<string, string> = {
  generated: k("Génération"),
  regenerated: k("Régénération"),
  finalized: k("Validation"),
  reopened: k("Réouverture"),
  restored: k("Restauration"),
  imported: k("Import"),
};

/** Motif d'une version, traduit ; un motif inconnu reste tel quel. */
function reasonLabel(reason: string): string {
  const label = REASONS[reason];
  return label ? t(label) : reason;
}

const SAVED: SaveSnapshot = { status: "saved", retryAt: null, blocked: null, message: null, unconfirmed: {}, offered: {} };
const savedSnapshot = () => SAVED;
const noSubscribe = () => () => {};

/** État de l'enregistrement dans l'en-tête de l'éditeur : « Enregistré automatiquement » seulement quand le serveur a
 *  tout confirmé ; sinon « Non enregistré », le délai du prochain essai et « Réessayer » (nouvel essai immédiat).
 *  Ordre finalisé entre-temps : « Réessayer » désigne « Rouvrir », l'action attendue ; licence expirée en lecture
 *  seule : il désigne le bandeau de la licence (le texte reste gardé sur ce poste et repart au renouvellement). */
function SaveState({ state, onRetry, onBlocked, oplan = false }: { state: SaveSnapshot; onRetry: () => void; onBlocked: () => void; oplan?: boolean }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (state.status !== "retrying") return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [state.status, state.retryAt]);
  const unsaved = state.status === "retrying" || state.status === "blocked";
  const seconds = Math.max(1, Math.ceil(((state.retryAt ?? now) - now) / 1000));
  const label =
    state.status === "saved"
      ? t("Enregistré automatiquement")
      : state.status === "saving"
        ? <Waiting>{t("Enregistrement…")}</Waiting>
        : state.status === "retrying"
          ? t("Non enregistré – nouvel essai dans {s} s", { s: seconds })
          : state.blocked === "finalized"
            ? oplan
              ? t("Non enregistré – OPLAN validé")
              : t("Non enregistré – OPO validé")
            : state.blocked === "read_only"
              ? t("Non enregistré – licence expirée")
              : t("Non enregistré");
  return (
    <>
      <span
        className={clsx("mr-1 truncate text-meta whitespace-nowrap", unsaved ? "text-warn" : "text-ink-muted")}
        title={unsaved ? (state.message ?? t("Votre texte est gardé sur ce poste ; nouvel essai automatique.")) : undefined}
      >
        {label}
      </span>
      <Presence show={unsaved}>
        {unsaved && (
          <Motion origin="fade" className="flex">
            <IconButton
              size="sm"
              title={t("Réessayer")}
              aria-label={t("Réessayer")}
              onClick={onRetry}
              blocked={state.blocked === "finalized" || state.blocked === "read_only"}
              onBlocked={state.blocked === "read_only" ? designateReadOnly : onBlocked}
            >
              <RefreshCw size={16} />
            </IconButton>
          </Motion>
        )}
      </Presence>
    </>
  );
}

/** OPO de la rotation CENTAC, comme le sélecteur de l'écran de l'ordre sur l'iPad ; partagé avec la carte, la
 *  DIREX, les modes d'action et la 3A (`Segmented` en onglets). */
export function OpoSelector({ value, onChange }: { value: number; onChange: (opo: number) => void }) {
  return (
    <Segmented
      role="tab"
      size="sm"
      fill={false}
      ariaLabel={t("OPO de la rotation")}
      className="shrink-0"
      value={String(value)}
      onChange={(v) => onChange(Number(v))}
      options={[1, 2, 3, 4].map((n) => ({ value: String(n), label: t("OPO {n}", { n }), title: t("OPO {n} de la rotation", { n }) }))}
    />
  );
}

/** Plan d'opération du corps d'armée (docs/08 §25) : document n° 0 du dossier de corps, même vue que l'OPO. */
export function OplanView() {
  return <OrderView doc="oplan" />;
}

/** Ordre d'opérations du dossier : pages identiques au PDF, édition par paragraphe, contrôle, exports. `doc`
 *  « oplan » : l'OPLAN du corps d'armée (docs/08 §25), source de vérité de l'OPO du corps. */
export function OrderView({ doc = "opo" }: { doc?: "opo" | "oplan" } = {}) {
  const exerciseId = useUi((s) => s.exerciseId);
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises.find((e) => e.id === exerciseId);
  const dossierAccess = useDossierAccess();
  const trialExport = useTrialExport();
  const isCentac = exercise?.parameters.level === "CENTAC";
  // Corps d'armée (docs/08 §24) : ordre au format du STANAG 2014, intitulé propre.
  const isCorps = exercise?.parameters.level === "CORPS";
  const mapOpo = useUi((s) => s.opo);
  // CENTAC : FRAGO ouvert à la place de l'OPO (docs/08 §27).
  const frago = useUi((s) => s.frago);
  const setFrago = useUi((s) => s.setFrago);
  // OPLAN du corps : document n° 0, qui précède l'OPO N° 1 (docs/08 §25).
  const oplan = doc === "oplan";
  const view: ViewKey = oplan ? "oplan" : "order";
  const setView = useUi((s) => s.setView);
  const number = oplan ? 0 : isCentac ? mapOpo : 1;
  const fragoOpen = isCentac && !oplan && frago !== null;
  // CENTAC : résultats ou WINGO de l'OPO ouverts à la place de l'OPO (frise de la rotation, 26/10/2026).
  const rotationPart = useUi((s) => s.rotationPart);
  const partOpen = isCentac && !oplan && !fragoOpen && rotationPart !== null;
  // Autre document de la rotation que l'OPO : l'en-tête ne porte plus les commandes de l'OPO.
  const otherOpen = fragoOpen || partOpen;
  const opened: Opened = fragoOpen
    ? { kind: "frago", opo: number, number: frago }
    : partOpen && rotationPart
      ? { kind: rotationPart, opo: number, number: rotationPart === "wingo" ? wingoNumber(number) : null }
      : { kind: "opo", opo: number, number: null };
  // OPLAN ouvert sur un dossier qui n'est pas de corps (vue mémorisée, autre dossier ouvert) : l'OPO le remplace.
  useEffect(() => {
    if (oplan && exercise && !isCorps) setView("order");
  }, [oplan, exercise, isCorps, setView]);
  const queryClient = useQueryClient();
  const [activeField, setActiveField] = useState<string | null>(null);
  // Tablette (sous 1280 px) : l'éditeur s'ouvre à la demande, par-dessus le document.
  const wide = useMediaQuery("(min-width: 1280px)");
  const [editorOpen, setEditorOpen] = useState(() => window.matchMedia("(min-width: 1280px)").matches);
  const [dialog, setDialog] = useState<null | "regenerate" | "signature" | "versions" | "transmit">(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const notify = useUi((s) => s.notify);
  const importInput = useRef<HTMLInputElement>(null);
  // Zone libre du dossier pas encore prête : l'ordre ne peut être ni rédigé ni recomposé (docs/14 §5.3) ; ces actions
  // désignent l'action attendue sur la fiche de la zone.
  const pendingZone = usePendingZone();
  const waitZone = () => designateZoneWait(pendingZone, "order");

  const orderKey = ["order", exerciseId, number];
  const orderQuery = useQuery({
    queryKey: orderKey,
    queryFn: () => api.order(exerciseId as string, number),
    enabled: !!exerciseId,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
    // L'OPO suit le dossier (docs/18 §5.7) : relu toutes les 20 s tant que la vue est visible, et au retour sur
    // l'onglet ; la saisie en cours reste prioritaire (brouillons de l'éditeur, file d'enregistrement). Lu dans
    // l'autre langue que sa langue de rédaction (docs/13) : relu toutes les 2,5 s tant qu'une traduction attend.
    refetchInterval: (query) =>
      translationInterval(
        Boolean(query.state.data?.translating?.length),
        typeof document !== "undefined" && document.visibilityState === "visible" ? 20_000 : false,
      ),
    refetchOnWindowFocus: true,
  });
  const order = orderQuery.data;
  const layoutQuery = useQuery({
    // Langue lue et traductions en cours (docs/13) : la mise en page suit la vue dans la langue du poste.
    queryKey: ["order-layout", exerciseId, number, order?.version, order?.has_signature, order?.language, (order?.translating ?? []).join(",")],
    queryFn: () => api.orderLayout(exerciseId as string, number),
    enabled: !!order,
    // Pendant le recalcul, la mise en page précédente du même ordre reste affichée ; jamais celle d'un autre
    // ordre (autre dossier, autre OPO de la rotation).
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[1] === exerciseId && prevQuery?.queryKey[2] === number ? prev : undefined,
  });
  // Apparition de l'OPO : la première page passe du flou au net quand elle se monte (ouverture de la vue,
  // changement d'ordre, première génération) et après chaque régénération ; jamais après une simple édition.
  // Après une régénération, l'effet part quand la nouvelle mise en page est arrivée, pas sur l'ancienne.
  const pagesRef = useRef<HTMLDivElement>(null);
  // Unités citées : leur nom survolé montre une mini-carte ; « Voir sur la carte » ouvre la carte centrée sur
  // l'unité, et « Retour à l'OPO » ramène à cet endroit de la lecture (09/10/2026).
  const deployment = useDeployment();
  const phase = useUi((s) => s.phase);
  const mentions = useMemo(() => unitMentions(deployment.data?.units, phase), [deployment.data, phase]);
  const [peek, setPeek] = useState<{ mention: UnitMention; anchor: DOMRect } | null>(null);
  const peekTimer = useRef<number | null>(null);
  const keepPeek = () => {
    if (peekTimer.current !== null) window.clearTimeout(peekTimer.current);
    peekTimer.current = null;
  };
  const hoverUnit = (mention: UnitMention | null, anchor: DOMRect | null) => {
    keepPeek();
    if (mention && anchor) {
      // Petite intention de survol : la carte ne surgit pas au simple passage de la souris.
      peekTimer.current = window.setTimeout(() => setPeek({ mention, anchor }), 180);
    } else peekTimer.current = window.setTimeout(() => setPeek(null), 220);
  };
  const showUnitOnMap = useUi((s) => s.showUnitOnMap);
  const orderReturn = useUi((s) => s.orderReturn);
  const clearOrderReturn = useUi((s) => s.clearOrderReturn);
  const [reveal, setReveal] = useState(0);
  const revealPending = useRef(false);
  const layoutReady = Boolean(layoutQuery.data) && !layoutQuery.isPlaceholderData;
  // Retour sur l'OPO après une modification ailleurs (carte, Données initiales) : l'ordre en cache est périmé. Les
  // pages attendent l'ordre relu et sa mise en page avant d'apparaître, au lieu de jouer l'apparition sur l'ancien
  // texte puis de sauter (au plus 4 s).
  const [awaitFresh, setAwaitFresh] = useState(() =>
    Boolean(queryClient.getQueryData(orderKey) && queryClient.getQueryState(orderKey)?.isInvalidated),
  );
  useEffect(() => {
    if (!awaitFresh) return;
    const timer = window.setTimeout(() => setAwaitFresh(false), 4000);
    return () => window.clearTimeout(timer);
  }, [awaitFresh]);
  useEffect(() => {
    if (!awaitFresh || orderQuery.isFetching || !layoutReady) return;
    setAwaitFresh(false);
    setReveal((n) => n + 1);
  }, [awaitFresh, orderQuery.isFetching, layoutReady]);
  useEffect(() => {
    revealPending.current = false;
  }, [order?.id]);
  useEffect(() => {
    if (!layoutReady || !revealPending.current) return;
    revealPending.current = false;
    setReveal((n) => n + 1);
  }, [layoutReady, layoutQuery.dataUpdatedAt]);
  const specsQuery = useQuery({
    queryKey: ["order-fields", exerciseId, number, order?.id],
    queryFn: () => api.orderFields(exerciseId as string, number),
    enabled: !!order,
    staleTime: Infinity,
  });
  const editableFields = useMemo(
    () => (specsQuery.data ? new Set(specsQuery.data.fields.map((f) => f.key)) : undefined),
    [specsQuery.data],
  );
  const versionsQuery = useQuery({
    queryKey: ["order-versions", exerciseId, number, order?.version],
    queryFn: () => api.orderVersions(exerciseId as string, number),
    enabled: !!order && dialog === "versions",
  });
  // Onglets de l'éditeur gardés pour l'avis de la carte, qui nomme les sections mises à jour.
  useEffect(() => {
    if (exerciseId && specsQuery.data) rememberOrderSpecs(exerciseId, number, specsQuery.data);
  }, [exerciseId, number, specsQuery.data]);

  // Paragraphes mis à jour d'après le dossier (cohérence carte ↔ OPO, 13/10/2026) : surlignés dans les pages et
  // l'éditeur jusqu'à la validation, la régénération ou « Masquer les repères ». Ceux qui sont plus récents que le
  // dernier « vu » de ce poste pulsent et ouvrent le bandeau de relecture (« Suivant » jusqu'au dernier).
  const changes = useMemo(() => orderChanges(order), [order]);
  const changeCount = Object.keys(changes).length;
  // Cascade des ordres : repères posés parce que l'ordre de l'échelon supérieur a changé (cause « chef ») ; le
  // bandeau le dit et nomme le dossier du chef s'il est lisible (déclinaison, sinon dossier émetteur transmis).
  const fromChief = changeCount > 0 && Object.values(changes).every((c) => c.cause === "chef");
  const declined = Boolean(exercise?.parameters.extra?.declination);
  const receivedQuery = useQuery({
    queryKey: ["declination", exerciseId],
    queryFn: () => background(() => api.declination(exerciseId as string)),
    enabled: Boolean(exerciseId && declined && fromChief),
    staleTime: 0,
  });
  const chiefName = chiefOf(exercise, receivedQuery.data?.received ?? null);
  // L'ordre a pu suivre celui du chef à son ouverture : le repère « l'ordre du chef a changé » est relu.
  useEffect(() => {
    if (exerciseId && declined && order?.version !== undefined) {
      void queryClient.invalidateQueries({ queryKey: ["declination", exerciseId] });
    }
  }, [exerciseId, declined, order?.version, queryClient]);
  const latest = latestChange(changes);
  const [seenTick, setSeenTick] = useState(0);
  const seen = useMemo(
    () => (exerciseId ? readSeen(licenceStorage, exerciseId, number) : null),
    [exerciseId, number, seenTick], // `seenTick` : relu après chaque « vu » rangé
  );
  const fresh = useMemo(() => new Set(freshKeys(changes, seen)), [changes, seen]);
  const markSeen = useCallback(() => {
    if (!exerciseId) return;
    writeSeen(licenceStorage, exerciseId, number, latest);
    setSeenTick((n) => n + 1);
  }, [exerciseId, number, latest]);
  const zoneChanges = useMemo(() => {
    const out = new Map<string, ZoneChange>();
    for (const [zone, keys] of changedZones(changes)) out.set(zone, { fresh: keys.some((key) => fresh.has(key)) });
    return out;
  }, [changes, fresh]);
  // Relecture des zones surlignées, de haut en bas : la zone courante vient au milieu de l'écran et pulse.
  const [cursor, setCursor] = useState(-1);
  useEffect(() => setCursor(-1), [order?.id, latest]);
  const goToChange = useCallback(
    (index: number) => {
      const zones = [...(pagesRef.current?.querySelectorAll<SVGGElement>("[data-change-zone]") ?? [])];
      if (!zones.length) return;
      const at = ((index % zones.length) + zones.length) % zones.length;
      const zone = zones[at]!;
      const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      zone.scrollIntoView?.({ block: "center", behavior: reduced ? "auto" : "smooth" });
      const tint = zone.querySelector<SVGGElement>(".opo-change-tint") ?? zone;
      if (!reduced && typeof tint.animate === "function") {
        tint.animate([{ opacity: 0.5 }, { opacity: 1 }, { opacity: 0.5 }], { duration: 700, iterations: 2, easing: "ease-in-out" });
      }
      setCursor(at);
      if (at === zones.length - 1) markSeen(); // dernière zone atteinte : tout est relu sur ce poste
    },
    [markSeen],
  );
  // « Masquer les repères » : les surlignages s'effacent en fondu, puis les repères tombent pour tout le dossier.
  const [hidingChanges, setHidingChanges] = useState(false);
  const hideChanges = useMutation({
    mutationFn: async () => {
      setHidingChanges(true);
      await new Promise((resolve) => window.setTimeout(resolve, 800));
      return api.orderChangesSeen(exerciseId as string, number);
    },
    onSuccess: (o) => {
      markSeen();
      queryClient.setQueryData(["order", exerciseId, number], o);
      // Les autres OPO du dossier (rotation CENTAC) perdent aussi leurs repères.
      void queryClient.invalidateQueries({ queryKey: ["order", exerciseId], predicate: (q) => q.queryKey[2] !== number });
    },
    onError: (err) =>
      notify({ text: err instanceof Error && err.message ? err.message : t("Opération impossible"), tone: "error" }),
    onSettled: () => setHidingChanges(false),
  });
  // Nouveaux repères pendant la lecture (autre poste, onglet de la carte) : la carte et l'ordre de bataille de ce
  // poste sont relus, ils montrent encore l'ancien dispositif.
  const latestSeenHere = useRef<string | null>(null);
  useEffect(() => {
    const before = latestSeenHere.current;
    latestSeenHere.current = latest;
    if (before !== null && latest && latest > before) {
      void queryClient.invalidateQueries({ queryKey: ["deployment"] });
      void queryClient.invalidateQueries({ queryKey: ["orbat"] });
    }
  }, [latest, queryClient]);
  // Statut de l'OPO changé hors de cette vue (remis en rédaction par une modification du dossier, ici ou sur un
  // autre poste) : la liste des dossiers (barre latérale, dossier complet) est relue.
  const statusSeen = useRef<string | null>(null);
  useEffect(() => {
    const before = statusSeen.current;
    statusSeen.current = order ? `${order.id}:${order.status}` : null;
    if (before && order && before.startsWith(`${order.id}:`) && before !== statusSeen.current) {
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
    }
  }, [order, queryClient]);
  // Ordre remis en rédaction (« Rouvrir », ou texte validé changé par le dossier) : sa dernière version le dit.
  const lastVersionQuery = useQuery({
    queryKey: ["order-versions", exerciseId, number, order?.version],
    queryFn: () => api.orderVersions(exerciseId as string, number),
    enabled: !!order && order.status === "draft" && changeCount > 0,
    staleTime: 60_000,
  });
  const reopenedNow = lastVersionQuery.data?.[0]?.reason === "reopened";

  const apply = (next: Order) => queryClient.setQueryData(orderKey, next);
  // Liste des dossiers relue : état de l'OPO (brouillon, validé) dans la barre latérale et le menu d'export.
  const refreshDossiers = () => {
    void queryClient.invalidateQueries({ queryKey: ["exercises"] });
    // Rotation CENTAC : l'état de l'OPO fait avancer la frise et l'étape (et les FRAGO, rédigés sur un OPO validé).
    if (isCentac) {
      void queryClient.invalidateQueries({ queryKey: ["rotation", exerciseId] });
      void queryClient.invalidateQueries({ queryKey: ["fragos", exerciseId] });
    }
  };
  // Échec d'une action : avis en ton d'erreur (les bandeaux sont réservés aux états durables).
  const fail = (err: unknown) =>
    notify({ text: err instanceof Error && err.message ? err.message : t("Opération impossible"), tone: "error" });

  // Cascade des ordres : dossiers subordonnés dont l'OPO a suivi cet ordre réécrit d'un geste (rédaction,
  // régénération, version restaurée, import) ; l'avis les nomme.
  const announceSubordinates = (o: Order) => {
    const note = subordinatesNote(o.subordinates);
    if (note) notify({ text: note, tone: "info" });
  };
  // Ordre (ré)écrit : génération, régénération, réécriture depuis le WINGO de la rotation CENTAC.
  const rewritten = (o: Order) => {
    // Première génération : les pages se montent avec l'effet. Régénération sans changement de texte : même
    // mise en page, l'effet part tout de suite ; sinon à l'arrivée de la nouvelle mise en page.
    if (order && o.version === order.version && o.has_signature === order.has_signature) setReveal((n) => n + 1);
    else if (order) revealPending.current = true;
    apply(o);
    setDialog(null);
    pagesRef.current?.scrollTo({ top: 0 });
    refreshDossiers();
    // ZEF et ZEA de la carte : celles de l'ordre réécrit.
    void queryClient.invalidateQueries({ queryKey: ["fire-zones", exerciseId] });
    designateAfterWrite(o); // OPO rédigé, en brouillon : sa validation est l'action attendue
    announceSubordinates(o);
  };
  const generate = useMutation({
    mutationFn: (body: { force?: boolean; keep_edits?: boolean }) => api.generateOrder(exerciseId as string, body, number),
    onSuccess: rewritten,
    onError: (err) => {
      if (err instanceof ApiError && err.code === "ORDER_EDITED") setDialog("regenerate");
      else if (!handleZoneRefusal(queryClient, err)) fail(err);
    },
  });
  // Enregistrement de l'éditeur : une file par dossier et par OPO (orderSaveQueue.ts), qui suit la version de son
  // ordre, renvoie d'elle-même ce que le serveur n'a pas confirmé et en garde une copie sur le poste.
  const queue = useMemo(
    () => (exerciseId ? orderSaveQueue({ exerciseId, number }, queryClient) : null),
    [exerciseId, number, queryClient],
  );
  const saveState = useSyncExternalStore(queue?.subscribe ?? noSubscribe, queue?.getSnapshot ?? savedSnapshot);
  // OPO modifié puis enregistré : « Valider l'OPO » est de nouveau désigné, une fois la frappe posée — sauf s'il
  // reste des paragraphes à relire (mis à jour d'après le dossier, ou à revoir) ou une signature à refaire.
  const savingSeen = useRef(false);
  const reviewPendingRef = useRef(false);
  useEffect(() => {
    const next = saveSettled(savingSeen.current, saveState.status);
    savingSeen.current = next.seen;
    if (!next.settled) return;
    const timer = window.setTimeout(() => {
      if (!reviewPendingRef.current) designateApproval();
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [saveState.status]);
  useEffect(() => {
    if (queue && order && order.exercise_id === exerciseId && order.number === number) queue.observe(order);
  }, [queue, order, exerciseId, number]);
  // Saisie non confirmée d'une session précédente (onglet fermé, réseau coupé) : rétablie à l'ouverture de l'ordre si
  // l'utilisateur ne l'a pas modifié depuis, sinon proposée (bandeau).
  useEffect(() => {
    if (queue && order && editableFields && order.exercise_id === exerciseId && order.number === number) {
      void queue.restore(order, editableFields);
    }
  }, [queue, order, editableFields, exerciseId, number]);
  const reset = useMutation({ mutationFn: (field: string) => api.resetOrderField(exerciseId as string, field, number), onSuccess: apply, onError: fail });
  // Paragraphe à revoir (docs/18 §5.4) : comparaison, « Garder mon texte » (le signalement est retiré).
  const [compare, setCompare] = useState<string | null>(null);
  // « Avant / après » d'un paragraphe mis à jour d'après le dossier (champ de l'éditeur).
  const [beforeAfterField, setBeforeAfterField] = useState<string | null>(null);
  const keep = useMutation({
    mutationFn: (field: string) => api.keepOrderField(exerciseId as string, field, number, orderQuery.data?.version ?? 0),
    onSuccess: (o) => {
      apply(o);
      setCompare(null);
    },
    onError: fail,
  });
  // OPO validé : le dossier complet est prêt si la fiche pays et la mise en ambiance sont rédigées (même
  // cache que leurs vues).
  const dossierReady = async (): Promise<[boolean, boolean]> => {
    const id = exerciseId as string;
    const written = async <T,>(key: readonly unknown[], fn: () => Promise<T>, ok: (data: T) => boolean) => {
      try {
        return ok(await queryClient.fetchQuery({ queryKey: key, queryFn: fn, staleTime: 30_000 }));
      } catch {
        return false;
      }
    };
    return Promise.all([
      written(countrySheetKey(id), () => api.countrySheet(id), sheetWritten),
      written(briefingKey(id), () => api.briefing(id), (b) => Boolean(b.sections?.some((x) => x.text))),
    ]);
  };
  const finalize = useMutation({
    // Saisie en attente envoyée d'abord : l'OPO validé est celui qui est à l'écran. Cohérence tactique (docs/24) :
    // des points restent ouverts (bloquants ou à revoir) ? le panneau s'ouvre d'office avant la validation ; il
    // rend la main (`confirmed`, passer outre justifié compris) pour valider.
    mutationFn: async (input: { confirmed?: boolean; override?: string } = {}) => {
      await queue?.whenSent();
      if (!input.confirmed) {
        const coherence = await api.coherence(exerciseId as string, Math.max(number, 1)).catch(() => null);
        if (gateNeeded(coherence)) throw new CoherenceGateSignal();
      }
      return api.finalizeOrder(exerciseId as string, number, input.override);
    },
    onSuccess: async (o) => {
      apply(o);
      refreshDossiers();
      if (oplan) {
        announceOplanApproved(notify, () => setView("order"));
        return;
      }
      const [country, part] = await dossierReady();
      announceApproved(country, part, notify, trialExport.blocked);
    },
    onError: (err) => {
      // Points de cohérence ouverts (ou constat bloquant relevé par le serveur) : panneau « Cohérence tactique ».
      if (err instanceof CoherenceGateSignal || (err instanceof ApiError && err.code === "COHERENCE_BLOCKING")) {
        openCoherenceGate({ kind: "approve", number, oplan });
        setView("coherence");
        return;
      }
      // Point bloquant du contrôle : le paragraphe s'ouvre dans l'éditeur et y est désigné.
      const blocking = blockingIssue(err, specsQuery.data);
      if (!blocking) return fail(err);
      setActiveField(blocking.field);
      setEditorOpen(true);
      designateIssue(blocking, notify, oplan);
    },
  });
  // Rouvert, l'ordre est aussitôt recomposé d'après le dossier (carte, composition, niveau changés depuis la
  // validation) et ses paragraphes changés depuis la signature sont surlignés : l'ordre, sa mise en page et les
  // champs de l'éditeur sont relus, « Suivant » est désigné s'il y a de quoi relire.
  // Cohérence tactique (docs/24) : validation confirmée dans le panneau (passer outre compris), puis rubrique de
  // l'ordre désignée par un constat, ouverte dans l'éditeur.
  const openCoherenceGate = useCoherence((s) => s.open);
  const confirmed = useCoherence((s) => s.confirmed);
  const fieldTarget = useCoherence((s) => s.fieldTarget);
  const finalizeMutate = finalize.mutate;
  useEffect(() => {
    if (!confirmed || !order) return;
    const taken = useCoherence.getState().takeConfirmed(number);
    if (taken) finalizeMutate({ confirmed: true, override: taken.override });
  }, [confirmed, order, number, finalizeMutate]);
  useEffect(() => {
    if (!fieldTarget || !order) return;
    const field = useCoherence.getState().takeField(number);
    if (!field) return;
    setActiveField(field);
    setEditorOpen(true);
    designateSoon(`[id="field-${field}"]`, "field", 2500);
  }, [fieldTarget, order, number]);
  const reopen = useMutation({
    mutationFn: () => api.reopenOrder(exerciseId as string, number),
    onSuccess: (o) => {
      apply(o);
      refreshOrders(queryClient, exerciseId);
      refreshDossiers();
      announceReopened(notify, Object.keys(o.changes ?? {}).length, subordinatesNote(o.subordinates), oplan);
    },
    onError: fail,
  });
  const sign = useMutation({
    mutationFn: (png: string) => api.signOrder(exerciseId as string, png, number),
    onSuccess: (o) => {
      apply(o);
      setDialog(null);
    },
    onError: fail,
  });
  const unsign = useMutation({
    mutationFn: () => api.unsignOrder(exerciseId as string, number),
    onSuccess: (o) => {
      apply(o);
      setDialog(null);
    },
    onError: fail,
  });
  // Version restaurée, fichier d'échange importé : les champs de l'éditeur sont relus (niveau de l'ordre).
  const refreshSpecs = () => void queryClient.invalidateQueries({ queryKey: ["order-fields", exerciseId] });
  const restore = useMutation({
    mutationFn: (n: number) => api.restoreOrderVersion(exerciseId as string, n, number),
    onSuccess: (o) => {
      apply(o);
      refreshSpecs();
      setDialog(null);
      announceSubordinates(o);
    },
    onError: fail,
  });
  const importOrder = useMutation({
    mutationFn: (payload: unknown) => api.importOrder(exerciseId as string, payload),
    onSuccess: (o) => {
      apply(o);
      refreshSpecs();
      announceSubordinates(o);
    },
    onError: fail,
  });

  // Arrivée par « Voir les changements » (avis de la carte) : une fois l'ordre recomposé mis en page, la lecture se
  // place sur le premier paragraphe mis à jour.
  useEffect(() => {
    if (!layoutReady || awaitFresh || !changeCount || !takeChangesFocus(number)) return;
    const frame = window.requestAnimationFrame(() => goToChange(0));
    return () => window.cancelAnimationFrame(frame);
  }, [layoutReady, awaitFresh, changeCount, number, goToChange, layoutQuery.dataUpdatedAt]);

  // Dossier en lecture seule pour ce compte (docs/17 §3.2) : rédiger désigne le bandeau de lecture seule.
  const dossierLocked = dossierAccess.readOnly;
  // Rotation CENTAC : un OPO N°2 à 4 que le WINGO de l'OPO d'avant n'a pas encore annoncé se rédige d'ordinaire
  // par ce WINGO ; l'action attendue est alors le bouton du guide (le présent bouton reste secondaire).
  const rotationQuery = useQuery({
    queryKey: ["rotation", exerciseId],
    queryFn: () => api.rotation(exerciseId as string),
    enabled: Boolean(exerciseId) && isCentac && !oplan,
  });
  const awaitingWingo = isCentac && !oplan && rotationQuery.data?.opos[number - 1]?.announced === false;
  // Rotation CENTAC (registre A119) : le numéro de la rotation jouée nomme la mission et les ordres ; sans lui, rédiger
  // ouvre les Données initiales sur son champ.
  const rotationMissing = isCentac && !oplan && Boolean(exercise) && !rotationCode(exercise?.parameters);
  const askRotation = () => showMissionField(CENTAC_ROTATION_FIELD, t("Saisissez le numéro de la rotation, par exemple 08/26 : il nomme le dossier, sa mission et ses ordres."));
  const fragosQuery = useQuery({
    queryKey: ["fragos", exerciseId],
    queryFn: () => api.fragos(exerciseId as string),
    enabled: Boolean(exerciseId) && isCentac && !oplan,
  });
  // Étape courante de la rotation : quand elle se fait sur l'OPO ouvert (« Valider l'OPO N°… », « Rédiger
  // l'OPO N°1 »), sa commande passe dans la ligne de l'étape, une seule fois à l'écran.
  const rotationStep = rotationQuery.data ? currentStep(rotationSteps(rotationQuery.data)) : null;
  const stepHere =
    isCentac && !oplan && !otherOpen && rotationStep && rotationQuery.data
      ? stepAction(rotationStep, rotationQuery.data, opened)
      : null;
  const fragoDraftOpen =
    fragoOpen && Boolean(fragosQuery.data?.opos.some((o) => o.fragos.some((f) => f.number === frago && f.status !== "finalized")));
  // Partie de la rotation ouverte (résultats, WINGO) : à la place de l'OPO, même avant sa rédaction.
  const partView =
    partOpen && exerciseId && rotationQuery.data ? (
      rotationPart === "results" ? (
        <ResultsView
          key={`${exerciseId}:${number}`}
          exerciseId={exerciseId}
          number={number}
          summary={rotationQuery.data}
          hosted={rotationStep?.kind === "saisir" && rotationStep.opo === number}
        />
      ) : (
        <WingoView key={`${exerciseId}:${number}`} exerciseId={exerciseId} opo={Math.min(number, 3)} summary={rotationQuery.data} />
      )
    ) : null;
  // `label` : rotation CENTAC, commande de l'étape (« Rédiger l'OPO N°1 »).
  const generateControl = (label?: string) => (
    <Button
      variant={awaitingWingo ? "secondary" : "primary"}
      data-attention="order-generate"
      // Rotation CENTAC : commande de l'étape courante, l'action attendue (elle pulse).
      highlight={Boolean(label) && !generate.isPending && !dossierLocked && !rotationMissing && pendingZone === null}
      onClick={() => generate.mutate({})}
      disabled={generate.isPending}
      blocked={dossierLocked || pendingZone !== null || rotationMissing}
      onBlocked={dossierLocked ? designateDossierReadOnly : rotationMissing ? askRotation : waitZone}
    >
      {generate.isPending ? <Waiting>{t("Rédaction…")}</Waiting> : label ?? (oplan ? t("Générer l'OPLAN") : t("Générer l'ordre"))}
    </Button>
  );
  const generateButton = generateControl();
  // CENTAC : la frise de la rotation remplace le sélecteur d'OPO dans la vue de l'ordre.
  const opoSelector = null;

  // Dossier absent, ordre à générer, chargement : en-tête de la vue et état vide dessous (R13).
  const notFound = orderQuery.error instanceof ApiError && orderQuery.error.status === 404;
  // Retour de la carte (« Retour à l'OPO ») : la lecture reprend où elle était. Avant tout retour anticipé
  // (règle des hooks : même nombre de hooks à chaque rendu).
  useEffect(() => {
    if (!orderReturn || orderReturn.view !== view || !layoutQuery.data) return;
    const frame = window.requestAnimationFrame(() => {
      pagesRef.current?.scrollTo({ top: orderReturn.scroll });
      clearOrderReturn();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [orderReturn, layoutQuery.data, clearOrderReturn]);

  if (!exerciseId || !order) {
    return (
      <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
        <ViewHeader view={view} context={exercise?.name} actions={exerciseId ? opoSelector : null} />
        {/* Rotation CENTAC : la frise et l'étape, même avant la rédaction de l'OPO affiché. */}
        {isCentac && exerciseId && !oplan && (
          <RotationTimeline
            exerciseId={exerciseId}
            opened={opened}
            documentAction={stepHere === "rediger" ? generateControl(t("Rédiger l'OPO N°{n}", { n: number })) : undefined}
            holdPulse={fragoDraftOpen}
          />
        )}
        {partView ?? (fragoOpen && exerciseId && frago !== null ? (
          <FragoView key={`${exerciseId}:${frago}`} exerciseId={exerciseId} number={frago} onBack={() => setFrago(null)} />
        ) : (
        <>
        <PendingMissionDraftBanner exercise={exercise} />
        <HigherOrderBanner exercise={exercise} />
        <ChiefOrderBanner exercise={exercise} />
        <div className="min-h-0 flex-1 overflow-y-auto">
          {!exerciseId ? (
            <DossierEmptyState view="order" kind="none">
              {t("Ouvrez ou créez un dossier dans les Données initiales : l'ordre d'opérations se compose à partir de sa carte, de ses missions et de son ennemi.")}
            </DossierEmptyState>
          ) : generate.isPending ? (
            // Première rédaction : aucune feuille encore, la carte d'attente seule.
            <GenerationWait
              note={
                oplan
                  ? t("Le plan se compose d'après le dossier : divisions, ennemi, phases, fiche pays, mise en ambiance.")
                  : t("L'ordre se compose d'après le dossier : dispositif, missions, ennemi, dates, météo.")
              }
            />
          ) : notFound || !orderQuery.isLoading ? (
            <EmptyState
              icon={FileText}
              title={
                isCentac
                  ? t("OPO {n} de la rotation", { n: number })
                  : oplan
                    ? t("Plan d'opération du corps d'armée")
                    : isCorps
                      ? t("Ordre d'opérations du corps d'armée")
                      : t("Ordre d'opérations")
              }
              action={oplan && !isCorps ? undefined : stepHere === "rediger" ? undefined : generateButton}
              note={pendingZone ? <ZoneWaitCallout className="text-left" /> : undefined}
            >
              {isCentac && number > 1
                ? t("L'OPO N°{n} est rédigé par le WINGO N°{w}, généré à la fin de l'OPO N°{prev} une fois ses résultats saisis : il reprend la rotation type du CENTAC et la carte, mises à jour d'après ces résultats. Vous pouvez aussi le rédiger dès maintenant ; le WINGO le mettra à jour.", { n: number, w: number * 10 + 1, prev: number - 1 })
                : isCentac
                ? t("L'OPO N°1 est rédigé d'après la rotation type du CENTAC (mission et missions de l'EDC et de l'EEI de chaque phase) et d'après la carte (positions, LIMA, ennemi de la FORAD). Vous pourrez ensuite le relire page par page, le modifier, le signer et le valider.")
                : oplan && !isCorps
                ? t("L'OPLAN n'existe qu'au niveau corps d'armée.")
                : oplan
                ? t("L'OPLAN 001 du corps est rédigé au format d'un plan d'opération OTAN d'après le dossier : situation, mission, directives du commandant et concept d'opération en jour J, exécution, soutien, commandement, avec ses annexes A, B, D et R. Il est la source de vérité de l'ordre d'opérations du corps, qui en est tiré.")
                : isCorps
                ? t("L'ordre du corps est rédigé au format OTAN des ordres d'opérations d'après la carte du corps (divisions, ennemi, fuseau, FSCL, PC), avec ses annexes A à F. Vous pourrez ensuite le relire page par page, le modifier, le contrôler, le signer, l'exporter et le décliner aux divisions.")
                : t("L'ordre est rédigé à partir du dossier (dispositif, missions, ennemi, dates, météo). Vous pourrez ensuite le relire page par page, le modifier, le contrôler, le signer et l'exporter.")}
            </EmptyState>
          ) : (
            <EmptyState variant="loading" title={t("Chargement de l'ordre…")} />
          )}
        </div>
        </>
        ))}
      </div>
    );
  }

  const finalized = order.status === "finalized";
  const toReview = outdatedFields(order, specsQuery.data);
  // Paragraphes modifiés que l'éditeur montre (une clé hors des champs de ce niveau n'est pas comptée).
  const edited = editedFields(order, specsQuery.data);
  const comparing = compare ? outdatedPair(order, compare) : null;
  const beforeAfterPair = beforeAfterField
    ? beforeAfter(order, changes, changedFields(changes).get(beforeAfterField) ?? [])
    : null;
  const unsaved = saveState.status === "retrying" || saveState.status === "blocked";
  const offeredCount = Object.keys(saveState.offered).length;
  const layout = layoutQuery.data;
  // La langue est figée dans l'ordre à sa rédaction : après un changement de langue du dossier, la
  // régénération est l'action attendue (bandeau et bouton signalés).
  const languageName = (code: string) => (code === "en" ? t("anglais") : t("français"));
  const languageMismatch = Boolean(exercise && order.language !== exercise.language);
  // Ordre rédigé dans l'autre langue, dont une traduction est en cours (docs/13) : attente animée, export différé.
  const translatingOrder = Boolean(order.translating?.length);
  const base = `/api/exercises/${exerciseId}/order`;
  const q = `number=${number}`;
  // Cartes des annexes (A4 : ZEF et ZEA ; C7 : pénétrantes et rocades ; corps : annexes B et D ; OPLAN : schéma
  // de manœuvre de l'annexe A) : recalculées sur l'état du dossier, leur
  // adresse suit la version de l'ordre et celle du dossier (unité déplacée, LIMA glissée, fuseau retracé).
  const imageUrl = orderImageUrl(base, number, order.version, exercise?.version ?? 0, order.language);
  const opordTitle = String(order.fields.opordTitle ?? "").trim();
  // Niveaux qui ont des subordonnés à qui transmettre l'OPO : GTIA (ses S/GTIA), brigade, division.
  const canTransmit = !oplan && (TRANSMITTING as readonly string[]).includes(exercise?.parameters.level ?? "");
  // Ordre composé sur le terrain précédent (zone libre pas prête) : ni export ni recomposition avant la zone.
  const stale = order.stale ?? layoutQuery.data?.stale ?? null;
  // Relecture d'abord : paragraphes mis à jour pas encore relus sur ce poste (« Suivant » pulse), ou paragraphes
  // modifiés à la main à revoir (leur pastille pulse).
  const unreviewed = finalized ? 0 : fresh.size;
  const reviewPending = !finalized && (unreviewed > 0 || toReview.length > 0);
  // Après un enregistrement, « Valider l'OPO » n'est désigné ni pendant la relecture, ni quand la signature est à
  // refaire (« Signer de nouveau » passe avant).
  reviewPendingRef.current = reviewPending || (!finalized && Boolean(order.signature_outdated));
  const changedSectionsText = specsQuery.data ? sectionList(changedSections(Object.keys(changes), specsQuery.data).sections) : "";
  const manualReopen = Object.values(changes).some((c) => c.cause === "reouverture");
  // Ordre de corps qui a suivi une retouche de son OPLAN (docs/08 §25).
  const fromOplan = changeCount > 0 && Object.values(changes).every((c) => c.cause === "oplan");
  const reviewText = reviewBannerText({
    count: changeCount,
    sections: changedSectionsText,
    reopened: reopenedNow,
    manualReopen,
    fromChief,
    chief: chiefName,
    fromOplan,
    oplan,
  });
  // Brouillon relu et enregistré : « Valider l'OPO » est l'action attendue (il pulse), sauf pendant une
  // génération, un enregistrement, ou quand une autre action passe avant (langue, zone, lecture seule).
  const expectApproval = approvalExpected({
    finalized,
    generating: generate.isPending,
    approving: finalize.isPending,
    saveStatus: saveState.status,
    stale: Boolean(stale),
    locked: dossierLocked,
    languageMismatch,
    reviewPending,
    signatureOutdated: Boolean(order.signature_outdated),
  });

  // Dossier en lecture seule pour le compte (docs/17 §3.2) : finaliser, rouvrir, signer, restaurer, importer et
  // transmettre désignent le bandeau ; depuis le menu « Plus », la liste se referme d'abord.
  const lockedProps = dossierLocked ? { blocked: true, onBlocked: designateDossierReadOnly } : {};
  const lockedMenuProps = dossierLocked
    ? {
        blocked: true,
        onBlocked: () => {
          setMoreOpen(false);
          window.requestAnimationFrame(designateDossierReadOnly);
        },
      }
    : {};
  // Régénérer un ordre finalisé : « Rouvrir » est l'action attendue (R12).
  const regenerate = () => (dossierLocked ? designateDossierReadOnly() : generate.mutate({}));
  // Depuis le menu « Plus », la liste se referme d'abord : « Rouvrir » est désigné une fois la liste partie.
  const reopenFirst = (fromMenu = false) => {
    if (fromMenu) {
      setMoreOpen(false);
      designateSoon(attentionSelector(ORDER_REOPEN));
    } else designate(attentionSelector(ORDER_REOPEN));
    notify({ text: oplan ? t("OPLAN validé : rouvrez-le d'abord") : t("OPO validé : rouvrez-le d'abord"), tone: "info" });
  };
  const openImport = () => importInput.current?.click();

  // Tablette (sous 1280 px) : seules « Valider l'OPO » (ou « Rouvrir ») et « Exporter » restent
  // libellées ; le reste passe dans « Plus » (R14).
  // Rotation CENTAC : les commandes secondaires de l'OPO restent rangées dans « Plus », même sur grand écran (une
  // seule action en avant : celle de l'étape).
  const secondary = wide && !isCentac ? (
    <>
      <Button
        size="sm"
        variant="ghost"
        onClick={regenerate}
        blocked={finalized || pendingZone !== null}
        onBlocked={() => (finalized ? reopenFirst() : waitZone())}
        disabled={generate.isPending}
        title={t("Rédiger à nouveau depuis le dossier")}
        highlight={languageMismatch && !finalized}
      >
        <RefreshCw size={14} /> {t("Régénérer")}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setView("coherence")} title={t("Ouvrir la cohérence tactique du dossier")}>
        <ShieldCheck size={14} /> {t("Cohérence")}
      </Button>
      {!finalized && (
        <Button size="sm" variant="ghost" onClick={() => setDialog("signature")} title={t("Signature manuscrite")} {...lockedProps}>
          <FileSignature size={14} /> {order.has_signature ? t("Signé") : t("Signature")}
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={() => setDialog("versions")} title={t("Versions de l'ordre")}>
        <History size={14} /> {t("Versions")}
      </Button>
      {canTransmit && (
        <Button size="sm" variant="ghost" onClick={() => setDialog("transmit")} title={t("Créer le dossier de chaque subordonné, cet OPO dans ses ordres reçus")} {...lockedProps}>
          <Send size={14} /> {t("Transmettre")}
        </Button>
      )}
    </>
  ) : (
    <Menu
      open={moreOpen}
      onClose={() => setMoreOpen(false)}
      align="end"
      width={280}
      ariaLabel={t("Plus d'actions")}
      trigger={
        <IconButton size="sm" active={moreOpen} onClick={() => setMoreOpen((o) => !o)} title={t("Plus d'actions")} aria-label={t("Plus d'actions")}>
          <MoreHorizontal size={16} />
        </IconButton>
      }
    >
      <MenuItem
        icon={RefreshCw}
        label={t("Régénérer")}
        hint={t("Rédiger à nouveau depuis le dossier")}
        onSelect={regenerate}
        blocked={finalized || pendingZone !== null}
        onBlocked={() => {
          if (finalized) reopenFirst(true);
          else {
            setMoreOpen(false);
            waitZone();
          }
        }}
        disabled={generate.isPending}
      />
      <MenuItem icon={ShieldCheck} label={t("Cohérence")} hint={t("Ouvrir la cohérence tactique du dossier")} onSelect={() => setView("coherence")} />
      {!finalized && (
        <MenuItem
          icon={FileSignature}
          label={order.has_signature ? t("Signé") : t("Signature")}
          hint={t("Signature manuscrite")}
          onSelect={() => setDialog("signature")}
          {...lockedMenuProps}
        />
      )}
      <MenuItem icon={History} label={t("Versions")} hint={t("Versions de l'ordre")} onSelect={() => setDialog("versions")} />
      {canTransmit && (
        <MenuItem
          icon={Send}
          label={t("Transmettre")}
          hint={t("Créer le dossier de chaque subordonné, cet OPO dans ses ordres reçus")}
          onSelect={() => setDialog("transmit")}
          {...lockedMenuProps}
        />
      )}
    </Menu>
  );

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
      <ViewHeader
        view={view}
        // Nom du dossier seul, comme dans les autres vues : le titre de l'OPORD (en tête de la feuille) tronquait l'en-tête
        // à côté des actions ; il reste dans l'info-bulle.
        context={
          exercise?.name ? (
            <span title={[exercise.name, opordTitle].filter(Boolean).join(" · ")}>{exercise.name}</span>
          ) : undefined
        }
        badges={
          otherOpen ? undefined : (
          <>
            <Badge tone={finalized ? "ok" : "neutral"}>{finalized ? t("Validé") : t("Brouillon")}</Badge>
            {wide && <Badge title={t("Ordre rédigé en {language}", { language: languageName(order.language) })}>{order.language === "en" ? "EN" : "FR"}</Badge>}
            {/* Corps d'armée (docs/08 §25) : l'OPO est tiré de l'OPLAN, source de vérité ; la pastille mène de l'un à
                l'autre. */}
            {isCorps && (
              <button
                type="button"
                className="focus-inset flex rounded-md"
                onClick={() => setView(oplan ? "order" : "oplan")}
                title={
                  oplan
                    ? t("L'ordre d'opérations du corps est tiré de ce plan : toute retouche y est reportée. Ouvrir l'OPO")
                    : t("Mission, intention, phases, missions des divisions, CCIR, relations de commandement, soutien et ennemi viennent de l'OPLAN 001 : retouchez-les dans l'OPLAN. Ouvrir l'OPLAN")
                }
              >
                <Badge tone="accent">{oplan ? t("Source de l'OPO") : t("Tiré de l'OPLAN 001")}</Badge>
              </button>
            )}
            {edited.length > 0 && toReview.length === 0 && (
              <Badge tone="accent">{plural(edited.length, t("paragraphe modifié"), t("paragraphes modifiés"))}</Badge>
            )}
            {/* Paragraphes mis à jour d'après le dossier : le compteur mène, à chaque appui, à la zone surlignée
                suivante. */}
            <Presence show={changeCount > 0 && !finalized}>
              {changeCount > 0 && !finalized && (
                <Motion origin="fade" className="flex">
                  <button
                    type="button"
                    data-attention="order-changes"
                    className="focus-inset flex rounded-md"
                    title={t("Mis à jour d'après le dossier (carte, unités, Données initiales) : zones surlignées dans les pages")}
                    onClick={() => goToChange(cursor + 1)}
                  >
                    <Badge>
                      <span className="mr-1.5 size-2 shrink-0 rounded-sm bg-mark" aria-hidden />
                      {plural(changeCount, t("paragraphe mis à jour"), t("paragraphes mis à jour"))}
                    </Badge>
                  </button>
                </Motion>
              )}
            </Presence>
            {/* Paragraphes modifiés à la main dont le texte généré a changé (docs/18 §6.7) : la pastille ouvre le
                premier dans l'éditeur ; elle pulse quand c'est la relecture attendue avant la validation. */}
            <Presence show={toReview.length > 0}>
              {toReview.length > 0 && (
                <Motion origin="fade" className="flex">
                  <button
                    type="button"
                    data-attention="order-outdated"
                    className={clsx("focus-inset flex rounded-md", unreviewed === 0 && "wayfinding")}
                    title={t("Le texte généré a changé depuis votre modification (composition, rôles, Données initiales…)")}
                    onClick={() => {
                      setActiveField(toReview[0] ?? null);
                      setEditorOpen(true);
                    }}
                  >
                    <Badge dot tone="warn">
                      {plural(toReview.length, t("paragraphe à revoir"), t("paragraphes à revoir"))}
                    </Badge>
                  </button>
                </Motion>
              )}
            </Presence>
            {/* Saisie pas encore confirmée par le serveur : visible aussi éditeur fermé (tablette). */}
            <Presence show={unsaved}>
              {unsaved && (
                <Motion origin="fade" className="flex">
                  <Badge tone="warn" title={t("Votre texte est gardé sur ce poste ; nouvel essai automatique.")}>{t("Non enregistré")}</Badge>
                </Motion>
              )}
            </Presence>
          </>
          )
        }
        actions={
          otherOpen ? (
            opoSelector
          ) : (
          <>
            {/* Sélecteur d'OPO de la rotation : en tête des actions, comme dans les modes d'action, la DIREX et la 3A. */}
            {opoSelector}
            {secondary}
            {finalized ? (
              <Button
                size="sm"
                variant="secondary"
                data-attention={ORDER_REOPEN}
                onClick={() => reopen.mutate()}
                disabled={reopen.isPending}
                {...lockedProps}
                title={oplan ? t("Rouvrir l'OPLAN pour le modifier : il repasse en brouillon") : t("Rouvrir l'OPO pour le modifier : il repasse en brouillon")}
              >
                <Unlock size={14} /> {t("Rouvrir")}
              </Button>
            ) : stepHere === "valider" ? null : (
              <ApproveButton
                onApprove={() => finalize.mutate({})}
                pending={finalize.isPending}
                disabled={generate.isPending}
                // Rotation CENTAC : l'action attendue est celle de l'étape ; un OPO validé hors de son tour ne pulse pas.
                expected={expectApproval && !isCentac}
                variant={isCentac ? "secondary" : "primary"}
                oplan={oplan}
                {...(dossierLocked ? lockedProps : { blocked: Boolean(stale), onBlocked: waitZone })}
              />
            )}
            <ExportMenu
              formats={["pdf", "docx", "odt", "txt", "json"]}
              href={(fmt) => `${base}/export/${fmt}?${q}`}
              title={t("Exporter l'ordre")}
              variant={isCentac ? "secondary" : undefined}
              blocked={Boolean(stale) || translatingOrder}
              onBlocked={stale ? waitZone : () => notify({ text: t("Traduction en cours : l'export sera possible dans un instant."), tone: "info" })}
            >
              <MenuItem icon={Upload} label={t("Importer un fichier d'échange…")} onSelect={openImport} {...lockedMenuProps} />
            </ExportMenu>
          </>
          )
        }
        panelToggle={
          otherOpen ? undefined : (
          <IconButton
            size="sm"
            active={editorOpen}
            title={editorOpen ? t("Masquer l'éditeur") : t("Afficher l'éditeur")}
            aria-label={editorOpen ? t("Masquer l'éditeur") : t("Afficher l'éditeur")}
            onClick={() => setEditorOpen((o) => !o)}
          >
            {editorOpen ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
          </IconButton>
          )
        }
      />
      <input
        ref={importInput}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          try {
            importOrder.mutate(JSON.parse(await file.text()));
          } catch {
            notify({ text: t("Fichier illisible : choisissez un fichier d'échange exporté par Generex Web."), tone: "error" });
          }
        }}
      />

      {/* Rotation CENTAC (26/10/2026) : frise de tous les documents de la rotation et étape courante, avec sa seule
          action ; « Valider l'OPO N°… » y passe quand l'étape se fait sur l'OPO ouvert. */}
      {isCentac && exerciseId && !oplan && (
        <RotationTimeline
          exerciseId={exerciseId}
          opened={opened}
          order={order}
          documentAction={
            stepHere === "valider" ? (
              <ApproveButton
                onApprove={() => finalize.mutate({})}
                pending={finalize.isPending}
                disabled={generate.isPending}
                expected={expectApproval}
                label={t("Valider l'OPO N°{n}", { n: number })}
                {...(dossierLocked ? lockedProps : { blocked: Boolean(stale), onBlocked: waitZone })}
              />
            ) : undefined
          }
          holdPulse={
            fragoDraftOpen ||
            (!otherOpen && !finalized && (reviewPending || Boolean(order.signature_outdated) || languageMismatch))
          }
          onRewritten={rewritten}
        />
      )}
      {partView ?? (fragoOpen && exerciseId && frago !== null ? (
        <FragoView key={`${exerciseId}:${frago}`} exerciseId={exerciseId} number={frago} onBack={() => setFrago(null)} />
      ) : (
      <>

      {/* Zone libre pas encore prête : ordre composé sur le terrain précédent, recomposé quand la zone sera prête ; le
          bouton mène à l'action attendue (préparation en cours, relance, espace à libérer). */}
      <Presence show={Boolean(stale)}>{stale && <ZoneWaitBanner message={stale} />}</Presence>
      <PendingMissionDraftBanner exercise={exercise} />
      {/* Dossier d'un subordonné : l'ordre de son chef a changé depuis la déclinaison (audit du 10/10/2026). */}
      <HigherOrderBanner exercise={exercise} />
      {/* Dossier transmis : l'ordre de son chef a changé depuis la transmission (ordres transmis prévenus). */}
      <ChiefOrderBanner exercise={exercise} />

      {/* L'ordre a suivi le dossier (carte, composition, Données initiales) : paragraphes surlignés pas encore relus
          sur ce poste. « Suivant » mène de l'un à l'autre (action attendue avant la validation), « Masquer les
          repères » les retire pour tout le dossier. OPO validé remis en rédaction : il est à valider de nouveau. */}
      <Presence show={!finalized && unreviewed > 0}>
        {!finalized && unreviewed > 0 && (
          <Banner
            tone="info"
            actions={
              <>
                <Button size="sm" variant="secondary" onClick={() => hideChanges.mutate()} disabled={hideChanges.isPending} {...lockedProps}>
                  {hideChanges.isPending ? <Waiting>{t("Masquage…")}</Waiting> : t("Masquer les repères")}
                </Button>
                <Button size="sm" data-attention={ORDER_CHANGES_NEXT} highlight={!hideChanges.isPending} onClick={() => goToChange(cursor + 1)}>
                  {fromChief && cursor < 0 ? t("Voir les changements") : t("Suivant")}
                </Button>
              </>
            }
          >
            {reviewText}
          </Banner>
        )}
      </Presence>

      {/* Signature apposée sur un texte que le dossier a changé depuis : elle est à refaire (« Signer de nouveau »). */}
      <Presence show={!finalized && Boolean(order.signature_outdated)}>
        {!finalized && order.signature_outdated && (
          <Banner
            tone="warn"
            actions={
              <Button size="sm" data-attention="order-sign" highlight={!reviewPending} onClick={() => setDialog("signature")} {...lockedProps}>
                <FileSignature size={14} /> {t("Signer de nouveau")}
              </Button>
            }
          >
            {t("Le texte de l'OPO a changé depuis sa signature : signez-le de nouveau.")}
          </Banner>
        )}
      </Presence>

      {/* Rotation CENTAC : OPO validé, déjà joué, jamais rouvert d'office ; le dispositif a changé depuis. */}
      <Presence show={finalized && Boolean(order.behind)}>
        {finalized && order.behind && (
          <Banner tone="info">
            {t("Le dispositif a changé depuis la validation de cet OPO déjà joué : il reste tel qu'il a été validé.")}
          </Banner>
        )}
      </Presence>

      {/* Texte non enregistré d'une session précédente, mais l'ordre a été modifié depuis par l'utilisateur (autre
          poste, régénération, version rétablie) : proposé, jamais imposé. */}
      <Presence show={offeredCount > 0 && !finalized}>
        {offeredCount > 0 && !finalized && (
          <Banner
            tone="warn"
            actions={
              <>
                <Button size="sm" variant="secondary" onClick={() => queue?.dropOffered()}>
                  {t("Garder le texte actuel")}
                </Button>
                <Button size="sm" onClick={() => queue?.adoptOffered()}>
                  {t("Rétablir ma saisie")}
                </Button>
              </>
            }
          >
            {t("Texte non enregistré retrouvé sur ce poste ({count}) : l'ordre a été modifié depuis. Le rétablir remplacera le texte actuel.", {
              count: plural(offeredCount, t("paragraphe"), t("paragraphes")),
            })}
          </Banner>
        )}
      </Presence>

      <Presence show={languageMismatch && Boolean(exercise)}>
        {languageMismatch && exercise && (
          <Banner
            tone="info"
            actions={
              !finalized && (
                <Button size="sm" onClick={regenerate} disabled={generate.isPending}>
                  <RefreshCw size={14} /> {t("Régénérer en {language}", { language: languageName(exercise.language) })}
                </Button>
              )
            }
          >
            {t("Cet ordre est rédigé en {from} ; le dossier est désormais en {to}.", {
              from: languageName(order.language),
              to: languageName(exercise.language),
            })}
            {finalized && ` ${t("Rouvrez l'ordre pour le régénérer dans la langue du dossier.")}`}
          </Banner>
        )}
      </Presence>

      <TranslationBanner
        exerciseId={exerciseId}
        translating={translatingOrder}
        untranslated={Boolean(order.untranslated?.length)}
      />

      <div className="relative flex min-h-0 flex-1">
        <div ref={pagesRef} className="min-h-0 flex-1 overflow-y-auto">
          {/* Voile de rédaction pendant la (ré)génération de l'ordre ; l'éditeur, à côté, reste hors du
              voile. */}
          <GenerationVeil
            active={generate.isPending}
            delay={250}
            note={t("L'ordre se compose d'après le dossier : dispositif, missions, ennemi, dates, météo.")}
          >
            <div className="mx-auto flex max-w-[860px] flex-col gap-10 px-8 py-10 max-sm:px-4">
              {(!layout || awaitFresh) && <EmptyState variant="loading" compact title={t("Mise en page…")} />}
              {!awaitFresh && layout?.pages.map((p, i) => (
                <OrderPage
                  // Première page : remontée à chaque apparition de l'ordre pour rejouer son animation.
                  key={i === 0 ? `reveal-${order.id}-${reveal}` : p.number}
                  className={i === 0 ? "opo-reveal" : undefined}
                  page={p}
                  layout={layout}
                  imageUrl={imageUrl}
                  activeField={activeField}
                  editable={!finalized}
                  editableFields={editableFields}
                  onPick={(field) => {
                    setActiveField(field);
                    setEditorOpen(true);
                  }}
                  units={mentions}
                  onUnitHover={hoverUnit}
                  changed={finalized ? undefined : zoneChanges}
                  hidingChanges={hidingChanges}
                />
              ))}
            </div>
          </GenerationVeil>
        </div>
        {/* Éditeur : volet latéral en ligne à partir de 1280 px, en surimpression avec voile en dessous (tablette),
            qui se ferme au toucher du voile et par Échap. */}
        <Presence show={editorOpen && Boolean(specsQuery.data)}>
          {editorOpen && specsQuery.data && (
            <SidePanel
              title={t("Éditer l'ordre")}
              icon={FilePen}
              onClose={() => setEditorOpen(false)}
              actions={<SaveState state={saveState} onRetry={() => queue?.retryNow()} onBlocked={() => reopenFirst()} oplan={oplan} />}
            >
              {queue && (
                <OrderEditor
                  // Un éditeur par dossier et par OPO : en changer verse d'abord la saisie en cours dans la file
                  // de l'ordre quitté, jamais dans celle du suivant.
                  key={`${exerciseId}:${number}`}
                  specs={specsQuery.data}
                  order={order}
                  activeField={activeField}
                  queue={queue}
                  unconfirmed={saveState.unconfirmed}
                  // Rétablissement après la réponse de l'envoi en cours : la saisie abandonnée ne peut plus l'écraser.
                  onReset={(field) => void queue.whenSent().then(() => reset.mutate(field))}
                  outdated={toReview}
                  frameReview={order.frame_review}
                  onCompare={setCompare}
                  changes={changes}
                  onBeforeAfter={setBeforeAfterField}
                />
              )}
            </SidePanel>
          )}
        </Presence>
      </div>

      </>
      ))}

      <Presence show={dialog === "regenerate"}>
        {dialog === "regenerate" && (
          <Dialog
            title={t("Régénérer l'ordre ?")}
            onClose={() => setDialog(null)}
            footer={
              <>
                <Button variant="secondary" onClick={() => setDialog(null)}>{t("Annuler")}</Button>
                <Button variant="secondary" onClick={() => generate.mutate({ keep_edits: true })} disabled={generate.isPending}>
                  {t("Conserver mes modifications")}
                </Button>
                <Button variant="danger" onClick={() => generate.mutate({ force: true })} disabled={generate.isPending}>
                  {generate.isPending ? <Waiting>{t("Régénération…")}</Waiting> : t("Tout régénérer")}
                </Button>
              </>
            }
          >
            <DialogBody>
              <p className="text-ui leading-relaxed text-ink-soft">
                {t(
                  "Vous avez modifié {count}. « Conserver mes modifications » rédige à nouveau le reste de l'ordre depuis le dossier ; « Tout régénérer » écrase aussi vos modifications (elles restent consultables dans les versions).",
                  // Clés hors des champs de ce niveau ignorées ; le décompte du serveur faute de mieux.
                  { count: plural(edited.length || order.edited.length, t("paragraphe"), t("paragraphes")) },
                )}
              </p>
            </DialogBody>
          </Dialog>
        )}
      </Presence>
      <Presence show={Boolean(comparing)}>
        {comparing && compare && (
          <CompareDialog
            title={term(specsQuery.data?.fields.find((f) => f.key === compare)?.label ?? compare)}
            mine={comparing.mine}
            generated={comparing.generated}
            busy={keep.isPending || reset.isPending}
            readOnly={finalized || dossierLocked}
            onTakeGenerated={() => {
              const field = compare;
              setCompare(null);
              // Même chemin que « Reprendre le texte généré » de l'éditeur : la saisie en attente est d'abord envoyée.
              void (queue?.whenSent() ?? Promise.resolve()).then(() => reset.mutate(field));
            }}
            onKeepMine={() => keep.mutate(compare)}
            onClose={() => setCompare(null)}
          />
        )}
      </Presence>
      {/* « Avant / après » d'un paragraphe mis à jour d'après le dossier : lecture seule. */}
      <Presence show={Boolean(beforeAfterPair)}>
        {beforeAfterPair && beforeAfterField && (
          <CompareDialog
            title={term(specsQuery.data?.fields.find((f) => f.key === beforeAfterField)?.label ?? beforeAfterField)}
            mine={beforeAfterPair.before}
            generated={beforeAfterPair.after}
            busy={false}
            readOnly
            labels={{
              title: t("Avant / après : {field}", { field: term(specsQuery.data?.fields.find((f) => f.key === beforeAfterField)?.label ?? beforeAfterField) }),
              before: t("Texte d'avant"),
              after: t("Texte à jour"),
            }}
            subtitle={t("Mis à jour d'après le dossier (carte, unités, Données initiales).")}
            onTakeGenerated={() => setBeforeAfterField(null)}
            onKeepMine={() => setBeforeAfterField(null)}
            onClose={() => setBeforeAfterField(null)}
          />
        )}
      </Presence>
      <Presence show={dialog === "signature"}>
        {dialog === "signature" && (
          <SignaturePad
            hasSignature={order.has_signature}
            onClose={() => setDialog(null)}
            onSave={(png) => sign.mutate(png)}
            onDelete={() => unsign.mutate()}
          />
        )}
      </Presence>
      <Presence show={dialog === "transmit" && Boolean(exerciseId)}>
        {dialog === "transmit" && exerciseId && (
          <TransmitDialog exerciseId={exerciseId} number={number} onClose={() => setDialog(null)} />
        )}
      </Presence>
      <Presence show={dialog === "versions"}>
        {dialog === "versions" && (
          <Dialog title={t("Versions de l'ordre")} onClose={() => setDialog(null)}>
            <DossierReadOnlyNote className="mx-4 mt-3" />
            <ul className="divide-y divide-line">
              {(versionsQuery.data ?? []).map((v) => (
                <li key={v.number} className="flex min-h-8 items-center gap-3 px-4 py-2.5">
                  <span className="w-8 font-mono text-ui font-semibold text-ink tabular-nums">v{v.number}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-ui leading-snug text-ink">{reasonLabel(v.reason)}</span>
                    <span className="block text-meta leading-snug text-ink-muted">
                      {new Date(v.created_at).toLocaleString(locale())}
                      {v.edited.length ? ` · ${plural(v.edited.length, t("paragraphe modifié"), t("paragraphes modifiés"))}` : ""}
                    </span>
                  </span>
                  {!finalized && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => restore.mutate(v.number)}
                      disabled={restore.isPending}
                      {...lockedProps}
                    >
                      {t("Rétablir")}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </Dialog>
        )}
      </Presence>
      {peek && (
        <UnitPeek
          mention={peek.mention}
          anchor={peek.anchor}
          onEnter={keepPeek}
          onLeave={() => hoverUnit(null, null)}
          onShowOnMap={(m) => {
            setPeek(null);
            showUnitOnMap({ code: m.code, lngLat: m.lngLat }, pagesRef.current?.scrollTop ?? 0, view);
          }}
        />
      )}
    </div>
  );
}
